import {
  ECartTypeId,
  ECountryIso,
  ECurrency,
  ELanguage,
} from '@ringana/configs';
import config from '../../config';
import LocalStorageHelper from './LocalStorageHelper';

interface ICookieOptions {
  expires: string;
  // we can't use backticks here, thanks ESLint for not checking this
  // eslint-disable-next-line quotes
  'max-age': number;
}

export default class CookieHelper {
  private static checkIfCookieIsAccepted(name: string, value: string, options: ICookieOptions | null): boolean {
    const acceptedCookiesRaw = CookieHelper.getCcnInteracted();
    if(!acceptedCookiesRaw) {
      const eventListener = (): void => {
        CookieHelper.setCookie(name, value, options, true);
        document.removeEventListener(`ccn-set-${name}`, eventListener);
      };
      document.addEventListener(`ccn-set-${name}`, eventListener);
      return false;
    }
    const acceptedCookies = JSON.parse(acceptedCookiesRaw);
    return !!acceptedCookies.find((accepted: string) => accepted === name);
  }

  public static checkIfExternalCookieIsAccepted(name: string, callback: (() => unknown) | false = false): boolean {
    const acceptedCookiesRaw = this.getCookie(`ccn-interacted`);
    if(!acceptedCookiesRaw || !acceptedCookiesRaw.includes(name)) {
      const eventListener = (): void => {
        if(callback) {
          callback();
        }
        document.removeEventListener(`ccn-set-${name}`, eventListener);
      };
      document.addEventListener(`ccn-set-${name}`, eventListener);
      return false;
    }
    const acceptedCookies = JSON.parse(acceptedCookiesRaw);
    return !!acceptedCookies.find((accepted: string) => accepted === name);
  }

  public static checkIfCookiesCanBeSet(): boolean {
    return !!CookieHelper.getCcnInteracted();
  }

  private static getCookie(name: string): string | false {
    const cookiesArray = document.cookie.split(`; `);
    const cookieArray = cookiesArray.filter(item => {
      const cookieKey = item.split(`=`)[0];
      return cookieKey === name;
    });
    if(cookieArray.length === 1) {
      return decodeURIComponent(cookieArray[0].split(`=`)[1]);
    }
    return false;
  }

  private static getCookieOptions(seconds: number): ICookieOptions {
    return {
      expires: new Date(Date.now() + (seconds * 1000)).toUTCString(),
      'max-age': seconds,
    };
  }

  private static removeCookie(name: string, useDomain = true, domain = config.cookie.domain): void {
    const domainString = useDomain ? `domain=${domain};` : ``;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;;path=/;${domainString};Secure;SameSite=lax`;
  }

  private static setCookie(name: string, value: string, options: ICookieOptions | null = null, ccnRetry = false): void {
    if(!ccnRetry && !CookieHelper.checkIfCookieIsAccepted(name, value, options)) {
      return;
    }

    let cookie = `${name}=${encodeURIComponent(value)};path=/;domain=${config.cookie.domain}`;

    if(options) {
      for(const option of Object.keys(options)) {
        cookie += `;${option}=${options[option as keyof ICookieOptions]}`;
      }
    }
    // set default duration
    if(!options || (options && !options.expires && !options[`max-age`])) {
      cookie += `;max-age=${config.cookie.maxAgeInSeconds};expires=${new Date(Date.now() + (config.cookie.maxAgeInSeconds * 1000)).toUTCString()};`;
    }

    // add secure flag
    cookie += window.location.protocol !== `http:` ? `Secure;` : ``;
    cookie += `SameSite=Lax`;

    document.cookie = cookie;
  }

  public static getAllCookieNames(): string[] {
    const cookiesArray = document.cookie.split(`; `);
    const cookieArray = cookiesArray.filter(item => {
      return item.split(`=`)[0];
    });
    return cookieArray.map(cookie => decodeURIComponent((cookie.split(`=`)[0])));
  }

  public static removeAll(): void {
    this.removeAuthToken();
    this.removeLastUsedCartId();
    this.removeCartSession();
    this.removeCartIdsForCheckout();
    this.removePersonalUseCartId();
    this.removeDeliveryAddressIso();
  }

  public static getCurrency(): ECurrency {
    return this.getCookie(`rin-currency`) as ECurrency || ECurrency.EUR;
  }

  public static removeCurrency(): void {
    this.removeCookie(`rin-currency`);
  }

  public static setCurrency(value: ECurrency): void {
    this.setCookie(`rin-currency`, value);
  }

  public static getDeliveryAddressIso(): ECountryIso {
    return this.getCookie(`rin-delivery-address-iso`) as ECountryIso;
  }

  public static removeDeliveryAddressIso(): void {
    this.removeCookie(`rin-delivery-address-iso`);
  }

  public static setDeliveryAddressIso(value: ECountryIso): void {
    this.setCookie(`rin-delivery-address-iso`, value);
  }

  public static getAuthToken(): string | false {
    return this.getCookie(`rin-token`);
  }

  public static removeAuthToken(): void {
    this.removeCookie(`rin-token`);
  }

  public static setAuthToken(value: string): void {
    const options = this.getCookieOptions(config.cookie.maxAgeInSeconds);
    this.setCookie(`rin-token`, value, options);
  }

  private static getCcnInteracted(): string {
    return CookieHelper.getCookie(`ccn-interacted`) || ``;
  }

  public static getLanguage(): ELanguage {
    let language: ELanguage = navigator.language.split(`-`)[0] as ELanguage ?? ELanguage.DE;

    if(CookieHelper.checkIfCookiesCanBeSet()) {
      language = CookieHelper.getCookie(`rin-language`) as ELanguage || ELanguage.DE;
    }
    return language;
  }

  public static removeLanguage(): void {
    CookieHelper.removeCookie(`rin-language`);
  }

  public static setLanguage(language: ELanguage): void {
    const oneYearInS = 365 * 60 * 60 * 24;
    const options = CookieHelper.getCookieOptions(oneYearInS);
    CookieHelper.setCookie(`rin-language`, language, options);
  }

  public static setCartIdsForCheckout(value: number[]): void {
    this.setCookie(`rin-carts-for-checkout`, value.toString());
  }

  public static getCartIdsForCheckout(): number[] {
    const cookieValue = this.getCookie(`rin-carts-for-checkout`);
    return cookieValue ? cookieValue.split(`,`).map(val => +val) : [];
  }

  public static removeCartIdsForCheckout(): void {
    this.removeCookie(`rin-carts-for-checkout`);
  }

  public static setCartSession(value: string): void {
    this.setCookie(`rin-session`, value);
  }

  public static getCartSession(): string | false {
    let session = this.getCookie(`rin-session`);
    if(session) {
      session = session.replace(`%7B`, `{`);
      session = session.replace(`%7D`, `}`);
    }
    return session;
  }

  public static removeCartSession(): void {
    this.removeCookie(`rin-session`);
  }

  public static setLastUsedCartId(value: number): void {
    this.setCookie(`rin-cart-id`, value.toString());
    const event = document.createEvent(`Event`);
    event.initEvent(`rin-cart-id-update`, false, true);
    document.dispatchEvent(event);
  }

  public static getLastUsedCartId(): number | undefined {
    const cartId = parseInt(this.getCookie(`rin-cart-id`) || `-1`, 10);
    return cartId === -1
      ? undefined
      : cartId;
  }

  public static removeLastUsedCartId(): void {
    this.removeCookie(`rin-cart-id`);
  }

  public static setPersonalUseCartId(value: number): void {
    this.setCookie(`rin-personal-use-cart-id`, value.toString());
  }

  public static getPersonalUseCartId(): number | undefined {
    const cartIdFromCookie = this.getCookie(`rin-personal-use-cart-id`);
    if(cartIdFromCookie) {
      return parseInt(cartIdFromCookie, 10);
    }
    const cartIdFromLocalStorage = LocalStorageHelper.getCart()?.shoppingCarts.find((el): boolean => el.cartTypeId === ECartTypeId.PERSONAL_USE)?.cartId;
    if(!cartIdFromLocalStorage) {
      return undefined;
    }
    this.setPersonalUseCartId(cartIdFromLocalStorage);
    return cartIdFromLocalStorage;
  }

  public static removePersonalUseCartId(): void {
    this.removeCookie(`rin-personal-use-cart-id`);
  }

  public static getPartnerApplication(): string | false {
    return this.getCookie(`rin-partner-application`);
  }

  public static setPartnerApplicationDone(): void {
    this.setCookie(`rin-partner-application`, `done`, { expires: `0`, 'max-age': 0 });
  }

  public static removePartnerApplication(): void {
    this.removeCookie(`rin-partner-application`);
  }

  public static getBouncerWebSocketConnectionJWT(): string | false {
    return this.getCookie(`rin-bouncer-waiting-token`);
  }

  public static removeBouncerWebSocketConnectionJWT(): void {
    this.removeCookie(`rin-bouncer-waiting-token`);
  }

  public static setBouncerWebSocketConnectionJWT(value: string): void {
    const oneDayInSeconds = 60 * 60 * 24;
    const options = this.getCookieOptions(oneDayInSeconds);
    this.setCookie(`rin-bouncer-waiting-token`, value, options);
  }

  public static getBouncerShoppingJWT(): string | false {
    return this.getCookie(`rin-bouncer-token`);
  }

  public static removeBouncerShoppingJWT(): void {
    this.removeCookie(`rin-bouncer-token`);
  }

  public static setBouncerShoppingJWT(value: string): void {
    const tenMinutesInSeconds = config.bouncer.config.endTimeOffsetInMinutes * 60;
    const options = this.getCookieOptions(tenMinutesInSeconds);
    this.setCookie(`rin-bouncer-token`, value, options);
  }

  public static getBouncerShoppingStartTime(): string | false {
    return this.getCookie(`rin-bouncer-shopping-starttime`);
  }

  public static removeBouncerShoppingStartTime(): void {
    this.removeCookie(`rin-bouncer-shopping-starttime`);
  }

  public static setBouncerShoppingStartTime(value: number): void {
    const tenMinutesInSeconds = config.bouncer.config.endTimeOffsetInMinutes * 60;
    const options = this.getCookieOptions(tenMinutesInSeconds);
    this.setCookie(`rin-bouncer-shopping-starttime`, value.toString(), options);
  }

  public static removeCartId(): void {
    this.removeCookie(`rin-cart-id`);
  }

  public static removeCart(): void {
    this.removeCartId();
    this.removeCartSession();
    this.removeCartIdsForCheckout();
  }

  public static getSession(): string | false {
    return this.getCookie(`rin-session`);
  }
}
